/**
 * DataMatrixGenerator - Component to generate REAL GS1 DataMatrix 2D barcodes
 * Uses bwip-js library for proper ECC200 encoding (ISO/IEC 16022)
 *
 * IMPORTANT: This component now generates SCANNABLE barcodes
 * Previous version used a fake hash-based pattern that could NOT be decoded
 */
import React, { useEffect, useRef, useState } from 'react';
// Use explicit browser subpath export for consistent resolution in all environments
// The default 'bwip-js' import resolves differently in node vs browser conditions
import bwipjs from 'bwip-js/browser';
import { QrCode, Download, Copy, Check, AlertCircle, RefreshCw } from 'lucide-react';

interface DataMatrixGeneratorProps {
  /** GS1 data string - can be with or without parentheses */
  data: string;
  /** Size in pixels */
  size?: number;
  /** Show data string below barcode */
  showData?: boolean;
  /** Callback when barcode image is generated */
  onGenerated?: (dataUrl: string) => void;
}

/**
 * Format GS1 data for DataMatrix encoding
 * KEEP parentheses - bwip-js gs1datamatrix needs them to identify AIs
 */
const formatForEncoding = (data: string): string => {
  // KEEP parentheses - bwip-js gs1datamatrix needs them to identify AIs
  // Just remove any manual FNC1 characters
  return data.replace(/\x1D/g, '');
};

export const DataMatrixGenerator: React.FC<DataMatrixGeneratorProps> = ({
  data,
  size = 150,
  showData = true,
  onGenerated,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (!data || !canvasRef.current) return;

    setIsGenerating(true);
    setError(null);

    try {
      const canvas = canvasRef.current;

      // Format data for encoding (remove parentheses)
      const encodingData = formatForEncoding(data);

      // Calculate scale based on size
      // Larger scale = more pixels per module = clearer barcode
      const scale = Math.max(3, Math.floor(size / 40));

      // Generate REAL DataMatrix using bwip-js
      bwipjs.toCanvas(canvas, {
        bcid: 'gs1datamatrix',  // GS1 DataMatrix with automatic FNC1
        text: encodingData,
        scale: scale,
        includetext: false,
        padding: 4,            // Quiet zone
      });

      // Convert to data URL
      const dataUrl = canvas.toDataURL('image/png');
      setImageDataUrl(dataUrl);
      onGenerated?.(dataUrl);
    } catch (err) {
      console.error('Error generating DataMatrix:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro ao gerar DataMatrix';
      setError(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  }, [data, size, onGenerated]);

  const handleDownload = () => {
    if (!imageDataUrl) return;

    const link = document.createElement('a');
    link.download = `datamatrix-gs1-${Date.now()}.png`;
    link.href = imageDataUrl;
    link.click();
  };

  const handleCopy = async () => {
    if (!imageDataUrl || !canvasRef.current) return;

    try {
      const canvas = canvasRef.current;
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve));
      if (blob) {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob }),
        ]);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy image:', err);
      // Fallback: copy data URL
      try {
        await navigator.clipboard.writeText(data);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        console.error('Failed to copy text:', e);
      }
    }
  };

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <QrCode className="w-12 h-12 text-gray-400 mb-2" />
        <p className="text-sm text-gray-500">Informe os dados para gerar o DataMatrix</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-gray-700 flex items-center gap-2">
          <QrCode className="w-5 h-5 text-amber-600" />
          DataMatrix (GS1) - Escaneável
        </h4>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCopy}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Copiar imagem"
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <Copy className="w-4 h-4 text-gray-500" />
            )}
          </button>
          <button
            onClick={handleDownload}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Download PNG"
          >
            <Download className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      <div className="flex flex-col items-center">
        {error ? (
          <div className="flex flex-col items-center p-4 text-red-500">
            <AlertCircle className="w-12 h-12 mb-2" />
            <p className="text-sm">{error}</p>
            <p className="text-xs text-gray-400 mt-2">
              Verifique se os dados GS1 estão no formato correto
            </p>
          </div>
        ) : isGenerating ? (
          <div className="flex flex-col items-center p-4">
            <RefreshCw className="w-12 h-12 text-amber-500 animate-spin mb-2" />
            <p className="text-sm text-gray-500">Gerando DataMatrix...</p>
          </div>
        ) : (
          <div className="bg-white p-4 rounded-lg border">
            <canvas
              ref={canvasRef}
              style={{ maxWidth: size, maxHeight: size }}
              className="block"
            />
          </div>
        )}

        {showData && (
          <div className="mt-3 w-full">
            <div className="text-xs text-gray-500 mb-1">Dados codificados (GS1):</div>
            <code className="block w-full bg-gray-100 px-3 py-2 rounded text-xs font-mono overflow-x-auto">
              {/* Show human-readable format */}
              {data.includes('(') ? data : formatAsHumanReadable(data)}
            </code>
          </div>
        )}

        <div className="mt-2 flex items-center gap-2">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            ECC200
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            ISO/IEC 16022
          </span>
        </div>

        <p className="mt-2 text-xs text-gray-500">
          DataMatrix GS1 - Leitura única com GTIN, Validade e Lote
        </p>
      </div>
    </div>
  );
};

/**
 * Format raw GS1 data as human-readable with parentheses
 */
function formatAsHumanReadable(rawData: string): string {
  // Common AI patterns
  const patterns = [
    { ai: '01', len: 14 },  // GTIN
    { ai: '17', len: 6 },   // Expiry date
    { ai: '11', len: 6 },   // Production date
    { ai: '10', len: 0 },   // Batch/Lot (variable)
    { ai: '21', len: 0 },   // Serial (variable)
  ];

  let result = '';
  let pos = 0;

  while (pos < rawData.length) {
    let matched = false;

    for (const pattern of patterns) {
      if (rawData.substring(pos, pos + 2) === pattern.ai) {
        const aiLen = 2;
        const dataLen = pattern.len > 0 ? pattern.len : rawData.length - pos - aiLen;

        // Find actual data length for variable fields
        let actualLen = dataLen;
        if (pattern.len === 0) {
          // Variable length - find next AI or end
          for (let i = pos + aiLen; i < rawData.length; i++) {
            const nextTwoChars = rawData.substring(i, i + 2);
            const isNextAI = patterns.some(p => p.ai === nextTwoChars);
            if (isNextAI) {
              actualLen = i - pos - aiLen;
              break;
            }
          }
        }

        const data = rawData.substring(pos + aiLen, pos + aiLen + actualLen);
        result += `(${pattern.ai})${data}`;
        pos += aiLen + actualLen;
        matched = true;
        break;
      }
    }

    if (!matched) {
      // Unknown AI - just add remaining characters
      result += rawData.substring(pos);
      break;
    }
  }

  return result || rawData;
}
